import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  LARGE_DOG_BREED_IDS,
  PRICE_CATEGORIES,
  PriceCategoryId,
  PriceItem,
  getSummaryPrice,
  itemHasDetails,
  itemNameKey,
  itemNoteKey,
  itemsForCategory,
  labelKeyPath,
  largeDogBreedKey
} from '../../data/prices';
import { trackBookClick, trackPricesClick } from '../../analytics';

const formatPrice = (
  price: string,
  priceFrom: boolean | undefined,
  fromLabel: string
): string => (priceFrom === true ? `${fromLabel} ${price}` : price);

const PriceAccordionItem: React.FC<{
  item: PriceItem;
  expanded: boolean;
  onToggle: () => void;
  fromLabel: string;
}> = ({ item, expanded, onToggle, fromLabel }) => {
  const { t } = useTranslation();
  const detailsId = useId();
  const hasDetails = itemHasDetails(item);
  const name = t(itemNameKey(item.id));
  const summary = getSummaryPrice(item);
  const showFromPrefix =
    item.priceFrom === true ||
    (item.price == null &&
      item.options != null &&
      item.options.length > 1 &&
      item.options[0].priceFrom !== true);

  const summaryDisplay =
    summary === ''
      ? ''
      : formatPrice(
          summary,
          item.priceFrom === true ||
            (showFromPrefix && item.options != null && item.options.length > 1),
          fromLabel
        );

  if (!hasDetails) {
    return (
      <div className="rounded-2xl bg-white px-5 py-4 text-left shadow-sm ring-1 ring-black/5">
        <div className="min-w-0">
          <p className="text-base font-semibold leading-snug text-ink">{name}</p>
          {summaryDisplay !== '' && (
            <p className="mt-1 text-lg font-semibold text-primary-deep">
              {summaryDisplay}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl text-left shadow-sm ring-1 transition-colors ${
        expanded
          ? 'bg-primary text-white ring-primary'
          : 'bg-white text-ink ring-black/5'
      }`}
    >
      <button
        type="button"
        className="flex w-full items-start gap-3 px-5 py-4 text-left"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={detailsId}
      >
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold leading-snug">{name}</p>
          {summaryDisplay !== '' && (
            <p
              className={`mt-1 text-lg font-semibold ${
                expanded ? 'text-white' : 'text-primary-deep'
              }`}
            >
              {summaryDisplay}
            </p>
          )}
        </div>
        <span
          className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${
            expanded ? 'bg-white/20 text-white' : 'bg-section-light text-ink'
          }`}
          aria-hidden="true"
        >
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </span>
      </button>

      {expanded && (
        <div
          id={detailsId}
          className="border-t border-white/20 px-5 pb-4 pt-3 text-sm leading-relaxed text-white/90"
        >
          {item.hasNote === true && (
            <p className="mb-2">{t(itemNoteKey(item.id))}</p>
          )}
          {item.options != null && item.options.length > 0 && (
            <ul className="m-0 list-none space-y-2 p-0">
              {item.options.map((opt) => (
                <li
                  key={`${item.id}-${opt.labelKey}`}
                  className="flex items-baseline justify-between gap-3"
                >
                  <span>{t(labelKeyPath(opt.labelKey))}</span>
                  <span className="shrink-0 font-semibold text-white">
                    {formatPrice(opt.price, opt.priceFrom, fromLabel)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const PriceList: React.FC = () => {
  const { t } = useTranslation();
  const [category, setCategory] = useState<PriceCategoryId>('fullComplex');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const activeMeta =
    PRICE_CATEGORIES.find((c) => c.id === category) ?? PRICE_CATEGORIES[0];
  const items = itemsForCategory(category);

  useEffect(() => {
    setExpandedId(null);
  }, [category]);

  return (
    <section
      id="pricesId"
      className="mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-12"
      aria-label={t('prices')}
    >
      <div className="mb-8 text-center md:mb-10">
        <span className="section-eyebrow">{t('pricesSection.eyebrow')}</span>
        <h2 className="section-title">{t('pricesSection.title')}</h2>
        <p className="mx-auto mt-3 max-w-2xl text-base text-muted">
          {t('pricesSection.lead')}
        </p>
      </div>

      <div
        className="mb-6 flex flex-wrap justify-center gap-2"
        role="tablist"
        aria-label={t('pricesSection.categoriesLabel')}
      >
        {PRICE_CATEGORIES.map((cat) => {
          const selected = cat.id === category;
          return (
            <button
              key={cat.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                setCategory(cat.id);
                trackPricesClick('prices_section', cat.id);
              }}
              className={`rounded-full px-3.5 py-2 text-[0.8125rem] font-semibold transition-colors sm:px-4 sm:py-2.5 sm:text-sm ${
                selected
                  ? 'bg-primary text-white shadow-md shadow-primary/25'
                  : 'bg-white text-ink ring-1 ring-black/10 hover:bg-mint/40'
              }`}
            >
              {t(cat.tabKey)}
            </button>
          );
        })}
      </div>

      {activeMeta.descriptionKey != null && (
        <p className="mx-auto mb-6 max-w-3xl text-center text-sm leading-relaxed text-body md:text-base">
          {t(activeMeta.descriptionKey)}
        </p>
      )}

      {category === 'largeDogs' ? (
        <div
          className="mx-auto max-w-3xl rounded-2xl bg-white px-5 py-5 text-left shadow-sm ring-1 ring-black/5 sm:px-8"
          role="tabpanel"
        >
          <p className="text-lg font-semibold text-primary-deep">170zł / h</p>
          <p className="mt-3 text-sm text-muted">
            {t('pricesSection.largeDogs.breedsHeading')}
          </p>
          <ul className="mt-3 grid list-none grid-cols-1 gap-x-8 gap-y-1.5 p-0 text-sm text-body sm:grid-cols-2">
            {LARGE_DOG_BREED_IDS.map((breedId) => (
              <li key={breedId}>· {t(largeDogBreedKey(breedId))}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4"
          role="tabpanel"
        >
          {items.map((item) => (
            <PriceAccordionItem
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              fromLabel={t('pricesSection.from')}
              onToggle={() => {
                setExpandedId((prev) => (prev === item.id ? null : item.id));
              }}
            />
          ))}
        </div>
      )}

      <div className="mt-10 text-center md:mt-12">
        <p className="mb-4 text-muted">{t('pricesSection.ctaHint')}</p>
        <Link
          to="/appointments"
          className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-hover"
          onClick={() => {
            trackBookClick('prices_section');
          }}
        >
          {t('header.bookAppointment')}
        </Link>
      </div>
    </section>
  );
};

export default PriceList;
